/**
 * Chat WebSocket Hook
 *
 * Manages the WebSocket connection for real-time chat features:
 * typing indicators, presence, message delivery, reactions, read receipts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type {
  ChatMessage,
  TypingUser,
  UserPresence,
  ChatWSEvent,
} from '../types/chat';

interface UseChatWebSocketOptions {
  tenantId: string | null;
  onNewMessage?: (message: ChatMessage) => void;
  onMessageUpdated?: (data: { id: string; content: string; is_edited: boolean }) => void;
  onMessageDeleted?: (data: { id: string; channel_id: string }) => void;
  onTyping?: (data: TypingUser) => void;
  onPresence?: (data: UserPresence) => void;
  onReaction?: (data: { message_id: string; emoji: string; user_id: number; action: string }) => void;
  onReadReceipt?: (data: { channel_id: string; user_id: number; last_message_id: string }) => void;
  onChannelUpdated?: (data: Record<string, unknown>) => void;
  onUnreadUpdate?: (data: { channel_id: string; unread_count: number; mention_count: number }) => void;
  onPinned?: (data: { channel_id: string; message_id: string; pinned: boolean }) => void;
}

interface UseChatWebSocketReturn {
  isConnected: boolean;
  sendTypingStart: (channelId: string) => void;
  sendTypingStop: (channelId: string) => void;
  markRead: (channelId: string, lastMessageId: string) => void;
  subscribeChannels: (channelIds: string[]) => void;
}

export function useChatWebSocket(options: UseChatWebSocketOptions): UseChatWebSocketReturn {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const connect = useCallback(() => {
    if (!options.tenantId || !token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/chat/${options.tenantId}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send auth message
        ws.send(JSON.stringify({ type: 'auth', token }));
      };

      ws.onmessage = (event) => {
        try {
          const msg: ChatWSEvent = JSON.parse(event.data);

          switch (msg.type) {
            case 'session.started':
              setIsConnected(true);
              // Start ping interval
              if (pingTimerRef.current) clearInterval(pingTimerRef.current);
              pingTimerRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'ping' }));
                }
              }, 30000);
              break;

            case 'message.new':
              optionsRef.current.onNewMessage?.(msg.data as ChatMessage);
              break;

            case 'message.updated':
              optionsRef.current.onMessageUpdated?.(
                msg.data as { id: string; content: string; is_edited: boolean }
              );
              break;

            case 'message.deleted':
              optionsRef.current.onMessageDeleted?.(
                msg.data as { id: string; channel_id: string }
              );
              break;

            case 'typing':
              optionsRef.current.onTyping?.(msg.data as TypingUser);
              break;

            case 'presence':
              optionsRef.current.onPresence?.(msg.data as UserPresence);
              break;

            case 'reaction.toggled':
              optionsRef.current.onReaction?.(
                msg.data as { message_id: string; emoji: string; user_id: number; action: string }
              );
              break;

            case 'read_receipt':
              optionsRef.current.onReadReceipt?.(
                msg.data as { channel_id: string; user_id: number; last_message_id: string }
              );
              break;

            case 'channel.updated':
              optionsRef.current.onChannelUpdated?.(msg.data as Record<string, unknown>);
              break;

            case 'unread.update':
              optionsRef.current.onUnreadUpdate?.(
                msg.data as { channel_id: string; unread_count: number; mention_count: number }
              );
              break;

            case 'pinned':
              optionsRef.current.onPinned?.(
                msg.data as { channel_id: string; message_id: string; pinned: boolean }
              );
              break;

            case 'pong':
              break;

            case 'error':
              console.error('[ChatWS] Server error:', msg.data);
              break;
          }
        } catch (e) {
          console.error('[ChatWS] Failed to parse message:', e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);

        // Reconnect after delay
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      console.error('[ChatWS] Connection error:', e);
    }
  }, [options.tenantId, token]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendTypingStart = useCallback((channelId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing.start', channel_id: channelId }));
    }
  }, []);

  const sendTypingStop = useCallback((channelId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing.stop', channel_id: channelId }));
    }
  }, []);

  const markRead = useCallback((channelId: string, lastMessageId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: 'channel.read', channel_id: channelId, last_message_id: lastMessageId })
      );
    }
  }, []);

  const subscribeChannels = useCallback((channelIds: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'channels.subscribe', channel_ids: channelIds }));
    }
  }, []);

  return {
    isConnected,
    sendTypingStart,
    sendTypingStop,
    markRead,
    subscribeChannels,
  };
}
